<script setup lang="ts">
import type { PropType } from 'vue';
import { useFieldProps } from '@precon-tools-suite-by-shared/shared-common-forms-ui';
import Listbox from 'primevue/listbox';
import SupplierbranchDisplay from './SupplierbranchDisplay.vue';
import type { SupplierbranchDisplayFragment } from './SupplierbranchDisplay.generated';
import { ComposedNameSpan } from '@precon-tools-suite-by-shared/shared-names-ui';
import MultiSelect from 'primevue/multiselect';

const props = defineProps({
    modelValue: {
        type: [Array, String] as PropType<Array<string> | string>,
        default: () => [],
    },
    label: {
        type: String,
        default: 'Selecteer leveranciers',
    },
    multiple: {
        type: Boolean,
        default: false,
    },
    options: {
        type: Array as PropType<Array<SupplierbranchDisplayFragment>>,
        default: () => [],
    },
    dropdown: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: string | Array<string>): void;
}>();

const { id, name, modelValue } = useFieldProps(props, emit);
const filterFields = ['name.full', 'name.short', 'clientRef'];

function idToNode(id: string): SupplierbranchDisplayFragment | undefined {
    return props.options.find((x) => x.id === id);
}
</script>

<template>
    <template v-if="dropdown">
        <MultiSelect
            v-bind="$attrs"
            :id="id + '-multi'"
            v-model="modelValue"
            :name="name"
            :options="options"
            option-value="id"
            data-key="id"
            :filter="true"
            :filter-fields="filterFields"
            display="chip"
        >
            <template #chip="{ value }">
                <ComposedNameSpan :node="idToNode(value)" />
            </template>
            <template #option="{ option }">
                <div
                    class="p-multiselect-supplier-option flex flex-grow-1 w-2rem"
                >
                    <SupplierbranchDisplay
                        :node="option"
                        client-ref
                        status
                        reliability
                    />
                </div>
            </template>
        </MultiSelect>
    </template>
    <template v-else>
        <Listbox
            v-bind="$attrs"
            :id="id"
            v-model="modelValue"
            :name="name"
            :options="options"
            option-value="id"
            data-key="id"
            :multiple="multiple"
            :filter="options.length > 10"
            :filter-fields="filterFields"
            :meta-key-selection="false"
            list-style="max-height:89%"
        >
            <template #option="{ option }">
                <SupplierbranchDisplay
                    :node="option"
                    client-ref
                    status
                    reliability
                />
            </template>
        </Listbox>
    </template>
</template>
